import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { type Dog } from '@/api/entities/dogs';
import { useUpdateDog } from './api/useUpdateDog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/ui/file-upload';
import { ImageUpload } from '@/components/ui/image-upload';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';

const editDogSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  longName: z.string().optional(),
  category: z.string().min(1, 'Category is required'),
  sex: z.string().min(1, 'Sex is required'),
  color: z.string().optional(),
  dob: z.string().min(1, 'Date of birth is required'),
  sire: z.string().optional(),
  dam: z.string().optional(),
  akc: z.string().optional(),
  ownedBy: z.string().optional(),
  hips: z.string().optional(),
  elbows: z.string().optional(),
  pra: z.string().optional(),
  eic: z.string().optional(),
  hpnk: z.string().optional(),
  content: z.string().optional(),
  coverImgUrl: z.string().optional(),
  hipAndElbowUrl: z.string().optional(),
  dnaUrl: z.string().optional(),
  pedigreeUrl: z.string().optional(),
  cardiacUrl: z.string().optional(),
  eyeClearanceUrl: z.string().optional(),
  imagesUrls: z.array(z.string()).optional(),
});

type EditDogFormData = z.infer<typeof editDogSchema>;

type Props = {
  dog: Dog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EditDogDialog({ dog, open, onOpenChange }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EditDogFormData>({
    resolver: zodResolver(editDogSchema),
    defaultValues: {
      name: '',
      longName: '',
      category: '',
      sex: '',
      color: '',
      dob: '',
      sire: '',
      dam: '',
      akc: '',
      ownedBy: '',
      hips: '',
      elbows: '',
      pra: '',
      eic: '',
      hpnk: '',
      content: '',
      coverImgUrl: '',
      hipAndElbowUrl: '',
      dnaUrl: '',
      pedigreeUrl: '',
      cardiacUrl: '',
      eyeClearanceUrl: '',
      imagesUrls: [],
    },
  });

  const mutation = useUpdateDog({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Dog updated successfully',
        });
        onOpenChange(false);
      },
      onError: (error: Error) => {
        console.error('Mutation error:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to update dog',
          variant: 'destructive',
        });
      },
    },
  });

  useEffect(() => {
    if (dog && open) {
      let dobFormatted = '';
      if (dog.dob) {
        try {
          const date = new Date(dog.dob);
          if (!isNaN(date.getTime())) {
            dobFormatted = date.toISOString().split('T')[0];
          }
        } catch {}
      }
      reset({
        name: dog.name || '',
        longName: dog.longName || '',
        category: dog.category || '',
        sex: dog.sex || '',
        color: dog.color || '',
        dob: dobFormatted,
        sire: dog.sire || '',
        dam: dog.dam || '',
        akc: dog.akc || '',
        ownedBy: dog.ownedBy || '',
        hips: dog.hips || '',
        elbows: dog.elbows || '',
        pra: dog.pra || '',
        eic: dog.eic || '',
        hpnk: dog.hpnk || '',
        content: dog.content || '',
        coverImgUrl: dog.coverImgUrl || '',
        hipAndElbowUrl: dog.hipAndElbowUrl || '',
        dnaUrl: dog.dnaUrl || '',
        pedigreeUrl: dog.pedigreeUrl || '',
        cardiacUrl: dog.cardiacUrl || '',
        eyeClearanceUrl: dog.eyeClearanceUrl || '',
        imagesUrls: dog.imagesUrls || [],
      });
    }
  }, [dog, open, reset]);

  const onSubmit = (data: EditDogFormData) => {
    if (!dog) {
      toast({
        title: 'Error',
        description: 'No dog selected',
        variant: 'destructive',
      });
      return;
    }

    const apiPayload: Record<string, unknown> = {
      name: data.name,
      longName: data.longName || undefined,
      category: data.category,
      sex: data.sex,
      color: data.color || undefined,
      dateOfBirth: data.dob || undefined, // Transform dob to dateOfBirth for API
      sire: data.sire || undefined,
      dam: data.dam || undefined,
      akc: data.akc || undefined,
      ownedBy: data.ownedBy || undefined,
      hips: data.hips || undefined,
      elbows: data.elbows || undefined,
      pra: data.pra || undefined,
      eic: data.eic || undefined,
      hpnk: data.hpnk || undefined,
      content: data.content || undefined,
      coverImgUrl: data.coverImgUrl || undefined,
      hipAndElbowUrl: data.hipAndElbowUrl || undefined,
      dnaUrl: data.dnaUrl || undefined,
      pedigreeUrl: data.pedigreeUrl || undefined,
      cardiacUrl: data.cardiacUrl || undefined,
      eyeClearanceUrl: data.eyeClearanceUrl || undefined,
      imagesUrls: data.imagesUrls || undefined,
    };
    Object.keys(apiPayload).forEach((key) => {
      if (apiPayload[key] === undefined) {
        delete apiPayload[key];
      }
    });
    mutation.mutate({ id: dog.id, input: apiPayload as Partial<Dog> });
  };

  const categoryValue = watch('category');
  const sexValue = watch('sex');
  const colorValue = watch('color');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Dog</DialogTitle>
          <DialogDescription>Update the dog's information below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input id="name" {...register('name')} placeholder="Enter dog's name" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="longName">Long Name</Label>
              <Input id="longName" {...register('longName')} placeholder="Enter long name" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">
                Category <span className="text-destructive">*</span>
              </Label>
              <Select value={categoryValue} onValueChange={(value) => setValue('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stud-boys">Stud Boy</SelectItem>
                  <SelectItem value="sires">Sire</SelectItem>
                  <SelectItem value="girls">Girl</SelectItem>
                  <SelectItem value="youngsters">Youngster</SelectItem>
                  <SelectItem value="foundation-labs">Foundation Lab</SelectItem>
                  <SelectItem value="retired-labs">Retired Lab</SelectItem>
                  <SelectItem value="in-memory">In Memory</SelectItem>
                </SelectContent>
              </Select>
              {errors.category && (
                <p className="text-sm text-destructive">{errors.category.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sex">
                Sex <span className="text-destructive">*</span>
              </Label>
              <Select value={sexValue} onValueChange={(value) => setValue('sex', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select sex" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
              {errors.sex && <p className="text-sm text-destructive">{errors.sex.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <Select value={colorValue || ''} onValueChange={(value) => setValue('color', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="yellow">Yellow</SelectItem>
                  <SelectItem value="chocolate">Chocolate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <DatePicker control={control} name="dob" label="Date of Birth" required />
              {errors.dob && <p className="text-sm text-destructive">{errors.dob.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sire">Sire</Label>
              <Input id="sire" {...register('sire')} placeholder="Enter sire" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dam">Dam</Label>
              <Input id="dam" {...register('dam')} placeholder="Enter dam" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="akc">AKC</Label>
              <Input id="akc" {...register('akc')} placeholder="Enter AKC number" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ownedBy">Owned By</Label>
              <Input id="ownedBy" {...register('ownedBy')} placeholder="Enter owner name" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hips">Hips</Label>
              <Input id="hips" {...register('hips')} placeholder="Enter hips" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="elbows">Elbows</Label>
              <Input id="elbows" {...register('elbows')} placeholder="Enter elbows" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pra">PRA</Label>
              <Input id="pra" {...register('pra')} placeholder="Enter PRA" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eic">EIC</Label>
              <Input id="eic" {...register('eic')} placeholder="Enter EIC" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hpnk">HPNK</Label>
              <Input id="hpnk" {...register('hpnk')} placeholder="Enter HPNK" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <RichTextEditor
              value={watch('content') || ''}
              onChange={(content: string) => {
                setValue('content', content);
              }}
              height={500}
            />
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-lg font-semibold">Files & Images</h3>

            <FileUpload
              control={control}
              name="coverImgUrl"
              label="Cover Image"
              keyPrefix="dogs"
              accept="image/*"
            />

            <FileUpload
              control={control}
              name="hipAndElbowUrl"
              label="Hip and Elbow File"
              keyPrefix="dogs"
            />

            <FileUpload control={control} name="dnaUrl" label="DNA File" keyPrefix="dogs" />

            <FileUpload
              control={control}
              name="pedigreeUrl"
              label="Pedigree File"
              keyPrefix="dogs"
            />

            <FileUpload control={control} name="cardiacUrl" label="Cardiac File" keyPrefix="dogs" />

            <FileUpload
              control={control}
              name="eyeClearanceUrl"
              label="Eye Clearance File"
              keyPrefix="dogs"
            />

            <ImageUpload
              control={control}
              name="imagesUrls"
              label="Photos"
              keyPrefix="dogs"
              description="Upload multiple images (JPG, PNG)"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {isSubmitting || mutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
